import * as _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import useFileMatchesForRevision from '../../../../../hooks/api/revisions/useFileMatchesForRevision';
import { isDeclaredLicense as isDeclaredLicenseFn, selectOptionFactory } from '../../../../../utils';
import type { RevisionFileMatch } from '../../../../../hooks/api/revisions/useFileMatchesForRevision';
import type { SelectOption } from '../../../../../types';

//  Ignore the declared license identifier.
const isNotDeclaredLicense = (match: RevisionFileMatch) => !isDeclaredLicenseFn(match);
//  Creates a SelectOption from the Match.
const toSelectOption = ({ id, path }: RevisionFileMatch) => selectOptionFactory(id, path);

/**
 * Thin wrapper around useFileMatchesForRevision, which prepares the
 * list of matches to be used in a Select component, as well as managing
 * which one is currently selected.
 *
 * When `matches` changes (ie: when they are re-fetched), the hook will
 * reset the selected match to be the to the first one.
 *
 * Same as with the main `useFileMatchesForRevision` hook, `revisionId`
 * may be undefined in cases where the issue details (or other data
 * source) has not loaded yet.
 */
function useSelectFileMatchForRevision(revisionId?: string, licenseId?: string) {
  const {
    isLoading,
    isError,
    data,
  } = useFileMatchesForRevision(revisionId, licenseId);
  //  State from the react query hook
  const matches = useMemo(() => data?.matches ?? [], [data?.matches]);
  const isCliLicenseScanWithMatchData = data?.cliLicenseScanWithMatchData ?? false;
  const isDeclaredLicense = useMemo(() => matches.some(isDeclaredLicenseFn), [matches]);

  //  State for selection
  const [selectedMatchId, setSelectedMatchId] = useState<string | null>(null);
  const selectedMatch = useMemo(() => matches.find(match => match.id === selectedMatchId), [matches, selectedMatchId]);

  //  Whenever matches changes & there is at least one match,
  //  reset the selected match to the first one.
  useEffect(() => {
    if (matches.length > 0) {
      const firstMatch = _.head(matches.filter(isNotDeclaredLicense)) as RevisionFileMatch;
      if (firstMatch) {
        setSelectedMatchId(firstMatch.id);
      }
    }
  }, [matches]);

  //  We don't want the declared license to appear in the dropdown,
  //  plus we need these to be SelectOptions to be used in the dropdown.
  const matchOptions = useMemo(() => matches
    .filter(isNotDeclaredLicense)
    .map(toSelectOption), [matches]);

  //  Handle when a new match is selected.
  const onSelectedMatchChange = useCallback(({ value: id }: SelectOption) => {
    if (matches && id !== selectedMatchId) {
      const nextMatch = matches.find(match => match.id === id);
      if (nextMatch) {
        setSelectedMatchId(nextMatch.id);
      }
    }
  }, [matches, selectedMatchId]);

  return {
    isLoading,
    isError,
    matches,
    isCliLicenseScanWithMatchData,
    matchOptions,
    isDeclaredLicense,
    selectedMatch,
    selectedMatchOption: selectedMatch ? toSelectOption(selectedMatch) : undefined,
    onSelectedMatchChange,
  };
}

export default useSelectFileMatchForRevision;
